import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { DEFAULT_REACTION_RULES, isSingleEmoji, validateReactionRules } from '@shared/reactions'
import { api, ApiError, type KnowledgeAnswerView, type KnowledgeDocumentView } from '../lib/api'
import { ReadOnlyNotice } from './ReadOnlyNotice'

const CONFIDENCE_TONE: Record<string, 'good' | 'warn' | 'bad' | undefined> = {
  strong: 'good',
  likely: 'warn',
  weak: 'bad',
  none: 'bad',
}

/**
 * What the workspace knows, and what it does not.
 *
 * The list of answers is the boring half. The half that earns the screen is the gap report:
 * nobody sits down and writes forty answers from imagination, but plenty of people will write
 * the five that customers asked eleven times last week — if something tells them what those
 * were.
 */
export function Knowledge() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<{ title: string } | null>(null)
  const [search, setSearch] = useState('')

  const data = useQuery({ queryKey: ['knowledge'], queryFn: api.knowledge, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['knowledge'] })

  const reindex = useMutation({ mutationFn: api.reindexKnowledge, onSuccess: refresh })
  const testModel = useMutation({ mutationFn: api.testEmbeddingModel })
  const remove = useMutation({ mutationFn: api.deleteAnswer, onSuccess: refresh })

  if (data.isPending) return <div className="cad-card" style={{ height: '240px' }} />
  if (data.isError) {
    const message = data.error instanceof ApiError ? data.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { answers, documents, gaps, model, canManage } = data.data!
  const unindexed = answers.filter((answer) => !answer.indexed).length

  const query = search.trim().toLowerCase()
  const visibleAnswers = query
    ? answers.filter(
        (answer) =>
          answer.title.toLowerCase().includes(query) ||
          answer.body.toLowerCase().includes(query) ||
          answer.keywords.some((keyword) => keyword.toLowerCase().includes(query)),
      )
    : answers

  return (
    <>
      {!canManage && <ReadOnlyNotice what="the knowledge base" />}
      <header className="stack gap-1">
        <span className="cad-eyebrow">Answers</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">Knowledge</h1>
          {canManage && !adding && (
            <button
              type="button"
              className="cad-btn"
              onClick={() => {
                setDraft(null)
                setAdding(true)
              }}
            >
              New answer
            </button>
          )}
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Write these once and your team — and any AI agent you connect — can draw on them
          when a customer asks. Nothing is sent to us or to anyone else: the search runs
          against your own database.
        </p>
      </header>

      {/* ------------------------------------------------------------ the model */}

      <section className="cad-card">
        <div className="stack gap-3">
          <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
            <h2 className="cad-section-title">How answers are found</h2>
            {model ? (
              <span className="cad-pill" data-tone="good">
                {model.model}
              </span>
            ) : (
              <span className="cad-pill">keywords only</span>
            )}
          </div>

          {model ? (
            <p className="cad-note">
              Searching by meaning as well as by words, using <b>{model.model}</b> on{' '}
              <b>{model.url}</b>. A question phrased differently to the answer will still find
              it. The model runs on your own machine — no answer or question leaves it.
            </p>
          ) : (
            <p className="cad-note">
              Searching by keywords only, which means a question worded differently to the
              answer will not find it. To search by meaning, run{' '}
              <a href="https://ollama.com" target="_blank" rel="noreferrer">
                Ollama
              </a>{' '}
              and set <code>OLLAMA_URL</code>. Everything stays on your own machine.
            </p>
          )}

          {canManage && (
            <div className="row gap-2 wrap">
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={testModel.isPending}
                onClick={() => testModel.mutate()}
              >
                {testModel.isPending ? 'Checking…' : 'Check the model'}
              </button>
              {model && (
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="quiet"
                  disabled={reindex.isPending || answers.length === 0}
                  onClick={() => reindex.mutate()}
                >
                  {reindex.isPending ? 'Rebuilding…' : 'Rebuild the index'}
                </button>
              )}
            </div>
          )}

          {testModel.data && (
            <p className="cad-note">
              {testModel.data.model} answered from {testModel.data.url}, returning{' '}
              {testModel.data.dimensions}-dimension vectors.
            </p>
          )}
          {testModel.error && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {testModel.error instanceof Error ? testModel.error.message : 'The check failed.'}
            </p>
          )}
          {reindex.data && <p className="cad-note">{reindex.data.indexed} answers re-indexed.</p>}
          {reindex.error && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {reindex.error instanceof Error ? reindex.error.message : 'That did not work.'}
            </p>
          )}

          {/* An answer saved while the model was down is findable by words but not by
              meaning, which is a real difference and worth saying out loud. */}
          {model && unindexed > 0 && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {unindexed} {unindexed === 1 ? 'answer is' : 'answers are'} not in the index and can
              only be found by keyword. Rebuild the index to include them.
            </p>
          )}
        </div>
      </section>

      <Tester />

      {/* ------------------------------------------------------------ documents */}

      <Documents documents={documents} canManage={canManage} onChanged={refresh} />

      {/* ------------------------------------------------------------ Notion / Confluence */}

      <KnowledgeConnectors onSynced={refresh} />

      {/* ------------------------------------------------------------ gaps */}

      {gaps.length > 0 && (
        <section className="cad-card">
          <div className="stack gap-3">
            <h2 className="cad-section-title">Asked, but not answered</h2>
            <p className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
              These came up more than once and nothing here answers them.
            </p>
            <div className="stack gap-3">
              {gaps.map((gap) => (
                <div
                  key={gap.phrase}
                  className="row between wrap gap-3"
                  style={{ alignItems: 'flex-start' }}
                >
                  <div className="stack gap-1" style={{ flex: 1, minWidth: '260px' }}>
                    <div className="row gap-2" style={{ alignItems: 'center' }}>
                      <b>{gap.phrase}</b>
                      <span className="cad-pill" data-tone="bad">
                        asked {gap.count}×
                      </span>
                    </div>
                    {gap.examples.map((example) => (
                      <span key={example} className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                        “{example}”
                      </span>
                    ))}
                  </div>
                  {canManage && (
                    <button
                      type="button"
                      className="cad-btn"
                      data-variant="quiet"
                      onClick={() => {
                        setDraft({ title: gap.examples[0] ?? gap.phrase })
                        setAdding(true)
                      }}
                    >
                      Answer this
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {adding && canManage && (
        <Editor
          // "Answer this" on one gap, then another, without closing in between leaves `adding`
          // true both times — no unmount happens, so the title field kept showing whichever
          // gap was clicked first. Keying on the draft forces a fresh instance per gap.
          key={draft?.title ?? 'blank'}
          initialTitle={draft?.title}
          onCancel={() => setAdding(false)}
          onSaved={() => {
            setAdding(false)
            void refresh()
          }}
        />
      )}

      {/* ------------------------------------------------------------ answers */}

      {answers.length > 0 && (
        <div className="cad-field" style={{ maxWidth: '360px' }}>
          <input
            className="cad-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, text, or keywords"
            aria-label="Search answers"
          />
        </div>
      )}

      {answers.length === 0 ? (
        <div className="cad-card">
          <p className="cad-muted">
            Nothing written yet. Saved answers are what auto-reply and the AI draft from.{' '}
            {canManage
              ? 'Start with the questions your team already answers most often.'
              : 'Your role cannot add them.'}
          </p>
        </div>
      ) : visibleAnswers.length === 0 ? (
        <div className="cad-card">
          <p className="cad-muted">Nothing matches “{search.trim()}”.</p>
        </div>
      ) : (
        <section className="cad-card">
          <div className="stack gap-3">
            <h2 className="cad-section-title">
              {query
                ? `${visibleAnswers.length} of ${answers.length} ${answers.length === 1 ? 'answer' : 'answers'}`
                : `${answers.length} ${answers.length === 1 ? 'answer' : 'answers'}`}
            </h2>
            <div className="stack gap-2">
              {visibleAnswers.map((answer, index) => (
                <div key={answer.id}>
                  <div
                    className="row between wrap gap-3"
                    style={{
                      alignItems: 'flex-start',
                      padding: '12px 0',
                      borderTop: index === 0 ? 'none' : '1px solid var(--cad-line)',
                      opacity: answer.enabled ? 1 : 0.55,
                    }}
                  >
                    <div className="stack gap-1" style={{ flex: 1, minWidth: '260px' }}>
                      <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                        <b>{answer.title}</b>
                        {!answer.enabled && <span className="cad-pill">off</span>}
                        {!answer.indexed && <span className="cad-pill">words only</span>}
                      </div>
                      <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                        {answer.body.slice(0, 140)}
                        {answer.body.length > 140 ? '…' : ''}
                      </span>
                      {answer.keywords.length > 0 && (
                        <div className="row gap-2 wrap">
                          {answer.keywords.map((keyword) => (
                            <span key={keyword} className="cad-pill">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {canManage && (
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        onClick={() => {
                          // A failed delete on one answer must not resurface as a stale error on
                          // whichever answer gets edited next — this mutation's state is shared
                          // across every row's editor.
                          remove.reset()
                          setEditing(editing === answer.id ? null : answer.id)
                        }}
                      >
                        {editing === answer.id ? 'Close' : 'Edit'}
                      </button>
                    )}
                  </div>

                  {editing === answer.id && canManage && (
                    <Editor
                      answer={answer}
                      onCancel={() => setEditing(null)}
                      onSaved={() => {
                        setEditing(null)
                        void refresh()
                      }}
                      onDelete={() => {
                        if (confirm(`Delete this saved answer?

Auto-reply stops using it immediately. This cannot be undone.`)) remove.mutate(answer.id)
                      }}
                      deleteError={remove.error}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <ReactionSettings />
    </>
  )
}

/** Asks the real endpoint, so what is shown is what an agent would actually be given. */
function Tester() {
  const [question, setQuestion] = useState('')
  const fieldId = useId()

  const ask = useMutation({ mutationFn: (q: string) => api.askKnowledge(q, false) })

  return (
    <section className="cad-card">
      <div className="stack gap-3">
        <h2 className="cad-section-title">Try a question</h2>
        <div className="row gap-2 wrap">
          <input
            id={fieldId}
            className="cad-input"
            style={{ flex: 1, minWidth: '260px' }}
            value={question}
            placeholder="How long does delivery take?"
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && question.trim()) ask.mutate(question)
            }}
          />
          <button
            type="button"
            className="cad-btn"
            disabled={!question.trim() || ask.isPending}
            onClick={() => ask.mutate(question)}
          >
            {ask.isPending ? 'Looking…' : 'Ask'}
          </button>
        </div>

        {ask.data && (
          <div className="stack gap-3">
            <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
              <span className="cad-pill" data-tone={CONFIDENCE_TONE[ask.data.confidence]}>
                {ask.data.confidence === 'none' ? 'no answer' : ask.data.confidence}
              </span>
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                {ask.data.usedModel ? 'searched by meaning and words' : 'searched by words only'}
              </span>
            </div>

            {ask.data.hits.length === 0 ? (
              <p className="cad-note">
                Nothing here answers that. It would be offered to an agent as “no suggestion”
                rather than as a guess, and it will show up in the gaps above once it has been
                asked more than once.
              </p>
            ) : (
              ask.data.hits.map((hit) => (
                <div key={hit.id} className="stack gap-1">
                  <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                    <b>{hit.title}</b>
                    {hit.via.map((via) => (
                      <span key={via} className="cad-pill">
                        {via}
                      </span>
                    ))}
                    {hit.similarity !== null && (
                      <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                        {Math.round(hit.similarity * 100)}% alike
                      </span>
                    )}
                  </div>
                  <div className="cad-bubble">{hit.body}</div>
                </div>
              ))
            )}
          </div>
        )}
        {ask.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {ask.error instanceof Error ? ask.error.message : 'That did not work.'}
          </p>
        )}
      </div>
    </section>
  )
}

/**
 * A PDF or Word document, chunked into knowledge entries on upload rather than typed out by
 * hand. Reuses the same R2 upload pipeline as a message attachment — the new part is what
 * happens after: the text is extracted, split, and each passage indexed exactly like a
 * hand-written answer.
 */
function Documents({
  documents,
  canManage,
  onChanged,
}: {
  documents: KnowledgeDocumentView[]
  canManage: boolean
  onChanged: () => void
}) {
  const fileId = useId()

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const media = await api.uploadMedia(file)
      return api.addKnowledgeDocument(media)
    },
    onSuccess: onChanged,
  })

  const remove = useMutation({
    mutationFn: (documentKey: string) => api.deleteKnowledgeDocument(documentKey),
    onSuccess: onChanged,
  })

  const onFile = (file: File | undefined) => {
    if (!file) return
    upload.mutate(file)
  }

  if (!canManage && documents.length === 0) return null

  return (
    <section className="cad-card">
      <div className="stack gap-3">
        <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
          <h2 className="cad-section-title">Documents</h2>
          {canManage && (
            <div>
              <input
                id={fileId}
                type="file"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                style={{ display: 'none' }}
                onChange={(e) => {
                  onFile(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
              <label htmlFor={fileId} className="cad-btn" data-variant="quiet" role="button">
                {upload.isPending ? 'Reading…' : 'Upload a PDF or Word document'}
              </label>
            </div>
          )}
        </div>
        <p className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
          Each document is split into passages and indexed the same way as an answer written by
          hand — no need to copy it in yourself.
        </p>

        {upload.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {upload.error instanceof ApiError || upload.error instanceof Error
              ? upload.error.message
              : 'That upload could not be read.'}
          </p>
        )}
        {remove.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {remove.error instanceof ApiError || remove.error instanceof Error
              ? remove.error.message
              : 'Could not remove that document.'}
          </p>
        )}
        {upload.data && (
          <p className="cad-note">
            {upload.data.chunks} passage{upload.data.chunks === 1 ? '' : 's'} added, {upload.data.indexed} indexed
            by meaning.
          </p>
        )}

        {documents.length === 0 ? (
          <p className="cad-muted">No documents uploaded yet.</p>
        ) : (
          <div className="stack gap-2">
            {documents.map((doc) => (
              <div key={doc.documentKey} className="row between wrap gap-2" style={{ alignItems: 'center' }}>
                <div className="stack gap-1">
                  <b>{doc.documentName}</b>
                  <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                    {doc.chunks} passage{doc.chunks === 1 ? '' : 's'}
                    {doc.indexed < doc.chunks ? ` · ${doc.chunks - doc.indexed} not indexed by meaning` : ''}
                  </span>
                </div>
                {canManage && (
                  <button
                    type="button"
                    className="cad-btn"
                    data-variant="text"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (confirm(`Delete "${doc.documentName}"?

Its content is removed from the knowledge base and the AI stops citing it. You will need to re-upload the file to restore it.`)) remove.mutate(doc.documentKey)
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * Notion and Confluence, connected with a token the customer generates in their own
 * workspace — Notion's "internal integration" secret, a Confluence API token — rather than an
 * OAuth app Wami would need to register and operate. Syncing pulls one page at a time by URL,
 * the same trust model as uploading a document: the customer picks what gets indexed rather
 * than granting a blanket crawl of a whole space.
 */
function KnowledgeConnectors({ onSynced }: { onSynced: () => void }) {
  const queryClient = useQueryClient()
  const data = useQuery({ queryKey: ['knowledge-connectors'], queryFn: api.knowledgeConnectors, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['knowledge-connectors'] })

  const [notionToken, setNotionToken] = useState('')
  const [confluenceSite, setConfluenceSite] = useState('')
  const [confluenceEmail, setConfluenceEmail] = useState('')
  const [confluenceToken, setConfluenceToken] = useState('')
  const [notionPageUrl, setNotionPageUrl] = useState('')
  const [confluencePageUrl, setConfluencePageUrl] = useState('')

  const connectNotion = useMutation({
    mutationFn: () => api.connectNotion(notionToken.trim()),
    onSuccess: () => {
      setNotionToken('')
      void refresh()
    },
  })
  const connectConfluence = useMutation({
    mutationFn: () => api.connectConfluence(confluenceSite.trim(), confluenceEmail.trim(), confluenceToken.trim()),
    onSuccess: () => {
      setConfluenceToken('')
      void refresh()
    },
  })
  const disconnect = useMutation({
    mutationFn: (source: 'notion' | 'confluence') => api.disconnectKnowledgeSource(source),
    onSuccess: refresh,
  })
  const syncNotion = useMutation({
    mutationFn: () => api.syncKnowledgePage('notion', notionPageUrl.trim()),
    onSuccess: () => {
      setNotionPageUrl('')
      onSynced()
    },
  })
  const syncConfluence = useMutation({
    mutationFn: () => api.syncKnowledgePage('confluence', confluencePageUrl.trim()),
    onSuccess: () => {
      setConfluencePageUrl('')
      onSynced()
    },
  })

  if (data.isPending) return null
  // Not `|| isError` — a section that deletes itself on failure is indistinguishable from a
  // section that was removed, so someone who connected Notion yesterday and cannot find it
  // today has no way to tell which happened.
  if (data.isError) {
    return (
      <section className="cad-card">
        <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
          <p className="cad-muted" style={{ margin: 0 }}>Notion &amp; Confluence settings could not be loaded.</p>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={() => void data.refetch()}>
            Try again
          </button>
        </div>
      </section>
    )
  }
  const { notion, confluence, canManage } = data.data!
  if (!canManage && !notion.configured && !confluence.configured) return null

  return (
    <section className="cad-card">
      <div className="stack gap-4">
        <div className="stack gap-1">
          <h2 className="cad-section-title">Notion &amp; Confluence</h2>
          <p className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
            Connect with a token you generate in your own workspace, then sync one page at a
            time by pasting its URL. Nothing is crawled automatically.
          </p>
        </div>

        {/* ---------------------------------------------------------- Notion */}
        <div className="stack gap-2" style={{ borderTop: '1px solid var(--cad-line)', paddingTop: '12px' }}>
          <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
            <b>Notion</b>
            <span className="cad-pill" data-tone={notion.configured ? 'good' : undefined}>
              {notion.configured ? 'connected' : 'not connected'}
            </span>
          </div>
          {canManage && !notion.configured && (
            <div className="row gap-2 wrap">
              <input
                className="cad-input"
                style={{ flex: 1, minWidth: '240px' }}
                type="password"
                placeholder="Internal integration secret"
                value={notionToken}
                onChange={(e) => setNotionToken(e.target.value)}
              />
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={!notionToken.trim() || connectNotion.isPending}
                onClick={() => connectNotion.mutate()}
              >
                {connectNotion.isPending ? 'Verifying…' : 'Connect'}
              </button>
            </div>
          )}
          {connectNotion.error && (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
              {connectNotion.error instanceof ApiError || connectNotion.error instanceof Error
                ? connectNotion.error.message
                : 'That could not be verified.'}
            </span>
          )}
          {notion.configured && (
            <div className="row gap-2 wrap">
              <input
                className="cad-input"
                style={{ flex: 1, minWidth: '240px' }}
                placeholder="Page URL or id"
                value={notionPageUrl}
                onChange={(e) => setNotionPageUrl(e.target.value)}
              />
              <button
                type="button"
                className="cad-btn"
                disabled={!notionPageUrl.trim() || syncNotion.isPending}
                onClick={() => syncNotion.mutate()}
              >
                {syncNotion.isPending ? 'Syncing…' : 'Sync page'}
              </button>
              {canManage && (
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="text"
                  disabled={disconnect.isPending}
                  onClick={() => {
                    if (confirm(`Disconnect Notion?

Pages already synced stay in the knowledge base, but nothing new will sync until you reconnect.`)) disconnect.mutate('notion')
                  }}
                >
                  Disconnect
                </button>
              )}
            </div>
          )}
          {syncNotion.error && (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
              {syncNotion.error instanceof ApiError || syncNotion.error instanceof Error
                ? syncNotion.error.message
                : 'That page could not be synced.'}
            </span>
          )}
          {disconnect.error && disconnect.variables === 'notion' && (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
              {disconnect.error instanceof ApiError || disconnect.error instanceof Error
                ? disconnect.error.message
                : 'Could not disconnect Notion.'}
            </span>
          )}
          {syncNotion.data && (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
              “{syncNotion.data.title}” synced — {syncNotion.data.chunks} passage
              {syncNotion.data.chunks === 1 ? '' : 's'}.
            </span>
          )}
        </div>

        {/* ---------------------------------------------------------- Confluence */}
        <div className="stack gap-2" style={{ borderTop: '1px solid var(--cad-line)', paddingTop: '12px' }}>
          <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
            <b>Confluence</b>
            <span className="cad-pill" data-tone={confluence.configured ? 'good' : undefined}>
              {confluence.configured ? 'connected' : 'not connected'}
            </span>
          </div>
          {canManage && !confluence.configured && (
            <div className="stack gap-2">
              <div className="row gap-2 wrap">
                <input
                  className="cad-input"
                  style={{ flex: 1, minWidth: '200px' }}
                  placeholder="https://yoursite.atlassian.net"
                  value={confluenceSite}
                  onChange={(e) => setConfluenceSite(e.target.value)}
                />
                <input
                  className="cad-input"
                  style={{ flex: 1, minWidth: '200px' }}
                  placeholder="Account email"
                  value={confluenceEmail}
                  onChange={(e) => setConfluenceEmail(e.target.value)}
                />
                <input
                  className="cad-input"
                  style={{ flex: 1, minWidth: '200px' }}
                  type="password"
                  placeholder="API token"
                  value={confluenceToken}
                  onChange={(e) => setConfluenceToken(e.target.value)}
                />
              </div>
              <div>
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="quiet"
                  disabled={
                    !confluenceSite.trim() ||
                    !confluenceEmail.trim() ||
                    !confluenceToken.trim() ||
                    connectConfluence.isPending
                  }
                  onClick={() => connectConfluence.mutate()}
                >
                  {connectConfluence.isPending ? 'Verifying…' : 'Connect'}
                </button>
              </div>
            </div>
          )}
          {connectConfluence.error && (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
              {connectConfluence.error instanceof ApiError || connectConfluence.error instanceof Error
                ? connectConfluence.error.message
                : 'Those credentials could not be verified.'}
            </span>
          )}
          {confluence.configured && (
            <div className="row gap-2 wrap">
              <input
                className="cad-input"
                style={{ flex: 1, minWidth: '240px' }}
                placeholder="Page URL or id"
                value={confluencePageUrl}
                onChange={(e) => setConfluencePageUrl(e.target.value)}
              />
              <button
                type="button"
                className="cad-btn"
                disabled={!confluencePageUrl.trim() || syncConfluence.isPending}
                onClick={() => syncConfluence.mutate()}
              >
                {syncConfluence.isPending ? 'Syncing…' : 'Sync page'}
              </button>
              {canManage && (
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="text"
                  disabled={disconnect.isPending}
                  onClick={() => {
                    if (confirm(`Disconnect Confluence?

Pages already synced stay in the knowledge base, but nothing new will sync until you reconnect.`)) disconnect.mutate('confluence')
                  }}
                >
                  Disconnect
                </button>
              )}
            </div>
          )}
          {syncConfluence.error && (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
              {syncConfluence.error instanceof ApiError || syncConfluence.error instanceof Error
                ? syncConfluence.error.message
                : 'That page could not be synced.'}
            </span>
          )}
          {disconnect.error && disconnect.variables === 'confluence' && (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
              {disconnect.error instanceof ApiError || disconnect.error instanceof Error
                ? disconnect.error.message
                : 'Could not disconnect Confluence.'}
            </span>
          )}
          {syncConfluence.data && (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
              “{syncConfluence.data.title}” synced — {syncConfluence.data.chunks} passage
              {syncConfluence.data.chunks === 1 ? '' : 's'}.
            </span>
          )}
        </div>
      </div>
    </section>
  )
}

function Editor({
  answer,
  initialTitle,
  onCancel,
  onSaved,
  onDelete,
  deleteError,
}: {
  answer?: KnowledgeAnswerView
  initialTitle?: string
  onCancel: () => void
  onSaved: () => void
  onDelete?: () => void
  deleteError?: unknown
}) {
  const id = useId()
  const [title, setTitle] = useState(answer?.title ?? initialTitle ?? '')
  const [body, setBody] = useState(answer?.body ?? '')
  const [keywords, setKeywords] = useState((answer?.keywords ?? []).join(', '))
  const [enabled, setEnabled] = useState(answer?.enabled ?? true)

  const input = {
    title: title.trim(),
    body: body.trim(),
    keywords: keywords.split(',').map((k) => k.trim()).filter(Boolean),
    enabled,
  }

  const save = useMutation({
    mutationFn: () => (answer ? api.updateAnswer(answer.id, input) : api.createAnswer(input)),
    onSuccess: onSaved,
  })

  return (
    <div
      className="stack gap-3"
      style={{ borderTop: '1px solid var(--cad-line)', paddingTop: '16px', marginBottom: '16px' }}
    >
      <div className="cad-field">
        <label htmlFor={`${id}-title`}>The question, as a customer would ask it</label>
        <input
          id={`${id}-title`}
          className="cad-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="How long does delivery take?"
        />
      </div>
      <div className="cad-field">
        <label htmlFor={`${id}-body`}>The answer</label>
        <textarea
          id={`${id}-body`}
          className="cad-input"
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Standard delivery takes three to five working days."
        />
      </div>
      <div className="cad-field">
        <label htmlFor={`${id}-keywords`}>Other words people use — optional</label>
        <input
          id={`${id}-keywords`}
          className="cad-input"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="shipping, dispatch, courier"
        />
        <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
          Product names and local terms a model would not know. These count for more than the
          words in the answer itself.
        </span>
      </div>

      <label className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>This answer is in use</span>
      </label>

      {save.error && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {save.error instanceof Error ? save.error.message : 'That did not save.'}
        </p>
      )}
      {deleteError ? (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {deleteError instanceof Error ? deleteError.message : 'Could not delete that.'}
        </p>
      ) : null}

      <div className="row gap-2 wrap">
        <button
          type="button"
          className="cad-btn"
          disabled={!input.title || !input.body || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : answer ? 'Save' : 'Add answer'}
        </button>
        <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
          Cancel
        </button>
        {onDelete && (
          <button type="button" className="cad-btn" data-variant="text" onClick={onDelete}>
            Delete
          </button>
        )}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- reactions */

/**
 * Closing a ticket by reacting to the message.
 *
 * An agent on a delivery round will not open a dashboard, but they will react with a tick.
 * The screen leads with who is allowed to do it, because that is the thing people are right
 * to worry about: a customer reacting to their own complaint changes nothing.
 */
function ReactionSettings() {
  const queryClient = useQueryClient()
  const data = useQuery({ queryKey: ['reaction-rules'], queryFn: api.reactionRules, retry: false })

  const [rules, setRules] = useState<
    { emoji: string; action: string; value: string; enabled: boolean }[] | null
  >(null)

  const save = useMutation({
    mutationFn: (next: typeof rules) =>
      api.saveReactionRules(
        (next ?? []).map((rule) => ({
          emoji: rule.emoji,
          action: rule.action,
          value: rule.value || undefined,
          enabled: rule.enabled,
        })),
      ),
    onSuccess: () => {
      setRules(null)
      void queryClient.invalidateQueries({ queryKey: ['reaction-rules'] })
    },
  })

  if (data.isPending) return null
  if (data.isError) {
    return (
      <section className="cad-card">
        <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
          <p className="cad-muted" style={{ margin: 0 }}>Reaction settings could not be loaded.</p>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={() => void data.refetch()}>
            Try again
          </button>
        </div>
      </section>
    )
  }
  const { actions, connected, canManage } = data.data!

  const current =
    rules ??
    (data.data!.rules.length > 0
      ? data.data!.rules.map((rule) => ({
          emoji: rule.emoji,
          action: rule.action,
          value: rule.value ?? '',
          enabled: rule.enabled,
        }))
      : DEFAULT_REACTION_RULES.map((rule) => ({
          emoji: rule.emoji,
          action: rule.action,
          value: rule.value ?? '',
          enabled: rule.enabled !== false,
        })))

  const problems = validateReactionRules(
    current.map((rule) => ({
      emoji: rule.emoji,
      action: rule.action as never,
      value: rule.value,
      enabled: rule.enabled,
    })),
  )

  const update = (index: number, patch: Partial<(typeof current)[number]>) =>
    setRules(current.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)))

  return (
    <section className="cad-card">
      <div className="stack gap-3">
        <h2 className="cad-section-title">Working a ticket from WhatsApp</h2>
        <p className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
          React to a customer's message from your own phone and the ticket behind it changes.
        </p>

        {/* The question everyone asks first, answered before they ask it. */}
        <p className="cad-note">
          Only your team's own numbers can do this. A customer reacting to their own message
          changes nothing, however they react.
        </p>

        {connected === 0 && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            No WhatsApp number is connected, so nothing here will fire yet.{' '}
            <a href="#numbers">Connect one</a>.
          </p>
        )}

        <div className="stack gap-2">
          {current.map((rule, index) => (
            <div key={index} className="row gap-2 wrap" style={{ alignItems: 'center' }}>
              <input
                className="cad-input"
                style={{ width: '5rem', textAlign: 'center', fontSize: 'var(--cad-text-lg)' }}
                aria-label="Emoji"
                value={rule.emoji}
                disabled={!canManage}
                onChange={(e) => update(index, { emoji: e.target.value })}
              />
              <select
                className="cad-input"
                style={{ flex: 1, minWidth: '200px' }}
                aria-label="What it does"
                value={rule.action}
                disabled={!canManage}
                onChange={(e) => update(index, { action: e.target.value })}
              >
                {Object.entries(actions).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {(rule.action === 'label' || rule.action === 'note' || rule.action === 'priority') && (
                <input
                  className="cad-input"
                  style={{ width: '11rem' }}
                  aria-label="Value"
                  value={rule.value}
                  disabled={!canManage}
                  placeholder={rule.action === 'priority' ? 'urgent' : 'Follow up'}
                  onChange={(e) => update(index, { value: e.target.value })}
                />
              )}
              <label className="row gap-1" style={{ alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={rule.enabled}
                  disabled={!canManage}
                  onChange={(e) => update(index, { enabled: e.target.checked })}
                />
                <span className="cad-muted">on</span>
              </label>
              {canManage && (
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="text"
                  onClick={() => setRules(current.filter((_, i) => i !== index))}
                >
                  Remove
                </button>
              )}
              {rule.emoji && !isSingleEmoji(rule.emoji) && (
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                  not a single emoji
                </span>
              )}
            </div>
          ))}
        </div>

        {problems.map((problem) => (
          <p key={problem} className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {problem}
          </p>
        ))}
        {save.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {save.error instanceof Error ? save.error.message : 'That did not save.'}
          </p>
        )}

        {canManage && (
          <div className="row gap-2 wrap">
            <button
              type="button"
              className="cad-btn"
              disabled={problems.length > 0 || save.isPending || rules === null}
              onClick={() => save.mutate(current)}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="cad-btn"
              data-variant="quiet"
              onClick={() => setRules([...current, { emoji: '', action: 'resolve', value: '', enabled: true }])}
            >
              Add a reaction
            </button>
            {rules !== null && (
              <button
                type="button"
                className="cad-btn"
                data-variant="text"
                onClick={() => setRules(null)}
              >
                Undo changes
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
